import { useInstance } from '@milkdown/react';
import { callCommand } from '@milkdown/utils';
import type { CmdKey } from '@milkdown/core';
import {
  toggleStrongCommand,
  toggleEmphasisCommand,
  toggleInlineCodeCommand,
  wrapInHeadingCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  wrapInBlockquoteCommand,
  createCodeBlockCommand,
  insertHrCommand,
} from '@milkdown/preset-commonmark';
import { toggleStrikethroughCommand } from '@milkdown/preset-gfm';
import { Button, Tooltip, Divider } from 'antd';

/**
 * Milkdown formatting toolbar.
 *
 * Must be rendered inside a MilkdownProvider so that useInstance() has access
 * to the editor context.
 */
export default function EditorToolbar() {
  const [loading, getInstance] = useInstance();

  function run<T>(cmd: CmdKey<T>, payload?: T) {
    const editor = getInstance();
    if (editor) editor.action(callCommand(cmd, payload));
  }

  return (
    <div className="editor-toolbar">
      {/* Text formatting */}
      <Tooltip title="Bold">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(toggleStrongCommand.key)}
        >
          <strong>B</strong>
        </Button>
      </Tooltip>
      <Tooltip title="Italic">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(toggleEmphasisCommand.key)}
        >
          <em>I</em>
        </Button>
      </Tooltip>
      <Tooltip title="Strikethrough">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(toggleStrikethroughCommand.key)}
        >
          <s>S</s>
        </Button>
      </Tooltip>
      <Tooltip title="Inline Code">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(toggleInlineCodeCommand.key)}
        >
          &lt;/&gt;
        </Button>
      </Tooltip>

      <Divider type="vertical" className="toolbar-divider" />

      {/* Headings */}
      <Tooltip title="Heading 1">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInHeadingCommand.key, 1)}
        >
          H1
        </Button>
      </Tooltip>
      <Tooltip title="Heading 2">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInHeadingCommand.key, 2)}
        >
          H2
        </Button>
      </Tooltip>
      <Tooltip title="Heading 3">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInHeadingCommand.key, 3)}
        >
          H3
        </Button>
      </Tooltip>

      <Divider type="vertical" className="toolbar-divider" />

      {/* Lists */}
      <Tooltip title="Bullet List">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInBulletListCommand.key)}
        >
          &#8226; List
        </Button>
      </Tooltip>
      <Tooltip title="Ordered List">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInOrderedListCommand.key)}
        >
          1. List
        </Button>
      </Tooltip>

      <Divider type="vertical" className="toolbar-divider" />

      {/* Block elements */}
      <Tooltip title="Blockquote">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(wrapInBlockquoteCommand.key)}
        >
          &#8220; Quote
        </Button>
      </Tooltip>
      <Tooltip title="Code Block">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(createCodeBlockCommand.key)}
        >
          {'{ } Block'}
        </Button>
      </Tooltip>
      <Tooltip title="Horizontal Rule">
        <Button
          className="toolbar-btn"
          size="small"
          type="text"
          disabled={loading}
          onClick={() => run(insertHrCommand.key)}
        >
          &#8212; HR
        </Button>
      </Tooltip>

      <style>{`
        .editor-toolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 2px;
          padding: 6px 8px;
          border-bottom: 1px solid var(--border-subtle);
        }

        .editor-toolbar .toolbar-btn {
          background: transparent;
          color: var(--text-secondary);
          border: none;
          font-size: 13px;
          padding: 2px 8px;
          line-height: 1.4;
        }

        .editor-toolbar .toolbar-btn:hover,
        .editor-toolbar .toolbar-btn:focus {
          color: var(--text-primary) !important;
          background: rgba(255, 255, 255, 0.08) !important;
        }

        .editor-toolbar .toolbar-btn:disabled {
          color: var(--text-muted) !important;
          background: transparent !important;
        }

        .editor-toolbar .toolbar-divider {
          border-inline-start-color: var(--border-primary);
          margin: 0 6px;
          height: 20px;
          top: 0;
        }

        @media (max-width: 768px) {
          .editor-toolbar {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
